import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams, useParams, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Users, Plus, Search, ArrowLeft, ChevronDown
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { teamAPI } from '../services';
import { btn } from '../../utils/buttonStyles';
import { useSquads } from '../../squads/hooks/useSquads';
import ConfirmModal from '../../components/ConfirmModal';
import TeamCardComponent from '../components/TeamCard';
import { sportTypeAPI } from '../../services/api';

const PAGE_SIZE = 12;

const TeamsListPage = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const { setBreadcrumbItems } = useBreadcrumb();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { groupId: groupIdParam, squadId } = useParams();
  const groupId = groupIdParam || squadId;
  const { getGroup } = useSquads(false);

  const isProfileTeamsPath = location.pathname.startsWith('/profile/teams');
  const ownerType = searchParams.get('ownerType') || (groupId ? 'group' : (isProfileTeamsPath ? 'user' : null));
  const ownerId = searchParams.get('ownerId') || groupId || (isProfileTeamsPath && user?.id ? String(user.id) : null);
  const isMyTeams = isProfileTeamsPath || (ownerType === 'user' && user?.id && ownerId === String(user.id));
  const isGroupTeams = ownerType === 'group' && ownerId;

  const showTabs = !isMyTeams && !isGroupTeams;
  const [activeTab, setActiveTab] = useState('my');

  const [teams, setTeams] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sportTypeId, setSportTypeId] = useState('');
  const [sportTypes, setSportTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, teamId: null, teamName: '' });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [groupData, setGroupData] = useState(null);

  // Debounce search to avoid firing on every keystroke
  const searchDebounceRef = useRef(null);

  useEffect(() => {
    sportTypeAPI.getAll().then(data => {
      setSportTypes(Array.isArray(data) ? data : []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (isGroupTeams && ownerId) {
      getGroup(ownerId).then(result => {
        if (result?.success && result?.data) setGroupData(result.data);
      }).catch(() => {});
    }
  }, [isGroupTeams, ownerId, getGroup]);

  const buildFilters = useCallback((pageNum) => {
    const filters = { page: pageNum, limit: PAGE_SIZE };
    if (searchTerm) filters.search = searchTerm;
    if (sportTypeId) filters.sportTypeId = sportTypeId;
    return filters;
  }, [searchTerm, sportTypeId]);

  const fetchTeams = useCallback(async (pageNum = 1, append = false) => {
    try {
      append ? setLoadingMore(true) : setLoading(true);

      const filters = buildFilters(pageNum);
      let result;

      if (ownerType && ownerId) {
        result = await teamAPI.getByOwner(ownerType, ownerId, filters);
      } else if (user?.id && isMyTeams) {
        result = await teamAPI.getMyTeams(user.id, filters);
      } else if (showTabs && activeTab === 'my') {
        result = await teamAPI.getMyTeams(user.id, filters);
      } else {
        result = await teamAPI.getAll(filters);
      }

      let newTeams = result?.data || [];
      const newPagination = result?.pagination || null;

      // Browse tab: exclude the current user's own teams
      if (showTabs && activeTab === 'browse' && user?.id) {
        newTeams = newTeams.filter(t => t.createdBy !== user.id);
      }

      setTeams(prev => append ? [...prev, ...newTeams] : newTeams);
      setPagination(newPagination);
    } catch (error) {
      console.error('Error fetching teams:', error);
      toast.error('Failed to load teams');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [ownerType, ownerId, user?.id, isMyTeams, showTabs, activeTab, buildFilters]);

  // Reset to page 1 and refetch when filters, tab, or context changes
  useEffect(() => {
    setPage(1);
    fetchTeams(1, false);
  }, [ownerType, ownerId, user?.id, searchTerm, sportTypeId, activeTab]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setSearchTerm(value), 400);
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchTeams(nextPage, true);
  };

  const handleDeleteTeam = (teamId, teamName) => {
    setConfirmModal({ isOpen: true, teamId, teamName });
  };

  const confirmDeleteTeam = async () => {
    if (!confirmModal.teamId) return;
    setDeleteLoading(true);
    try {
      await teamAPI.delete(confirmModal.teamId);
      setPage(1);
      fetchTeams(1, false);
      toast.success('Team deleted successfully');
    } catch (error) {
      console.error('Error deleting team:', error);
      toast.error('Failed to delete team');
    } finally {
      setDeleteLoading(false);
      setConfirmModal({ isOpen: false, teamId: null, teamName: '' });
    }
  };

  const isOwner = (team) => {
    return user && (
      team.createdBy === user.id ||
      team.ownerId === String(user.id) ||
      team.owner_id === String(user.id)
    );
  };

  const getBaseUrl = () => {
    if (groupId) return `/squads/${groupId}/teams`;
    if (isMyTeams) return '/profile/teams';
    return '/teams';
  };
  const baseUrl = getBaseUrl();

  const getCreateUrl = () => {
    if (groupId) return `/squads/${groupId}/teams/new`;
    if (isMyTeams) return '/profile/teams/new';
    if (ownerType && ownerId) return `${baseUrl}/new?ownerType=${ownerType}&ownerId=${ownerId}`;
    return '/teams/new';
  };

  const pageInfo = useMemo(() => {
    if (isGroupTeams) {
      const groupName = groupData?.name || groupData?.groupName || 'Squad';
      return {
        title: `${groupName} Teams`,
        description: `Teams belonging to ${groupName}`,
        emptyMessage: 'No teams in this squad yet',
        emptyDescription: 'Create a team for this squad to get started'
      };
    }
    if (isMyTeams) {
      return {
        title: 'My Teams',
        description: 'Teams you own or manage',
        emptyMessage: 'No teams yet',
        emptyDescription: 'Create your first team to get started'
      };
    }
    if (activeTab === 'my') {
      return {
        title: 'Teams',
        description: 'Teams you own or manage',
        emptyMessage: 'No teams yet',
        emptyDescription: 'Get started by creating your first team'
      };
    }
    return {
      title: 'Teams',
      description: 'Discover teams from other players',
      emptyMessage: 'No teams to browse',
      emptyDescription: 'Check back later for teams from other players'
    };
  }, [isGroupTeams, isMyTeams, groupData, activeTab]);

  useEffect(() => {
    let items = [];
    if (groupId || isGroupTeams) {
      const groupName = groupData?.name || groupData?.groupName || 'Squad';
      items = [
        { label: 'Home', path: '/' },
        { label: 'Squads', path: '/squads' },
        { label: groupName, path: `/squads/${ownerId}` },
        { label: 'Teams' }
      ];
    } else if (isMyTeams || location.pathname.startsWith('/profile')) {
      items = [
        { label: 'Home', path: '/' },
        { label: 'Profile', path: '/profile' },
        { label: 'Teams' }
      ];
    } else {
      items = [{ label: 'Home', path: '/' }, { label: 'Teams' }];
    }
    setBreadcrumbItems(items);
    return () => setBreadcrumbItems([]);
  }, [groupId, isGroupTeams, isMyTeams, groupData, ownerId, location.pathname, setBreadcrumbItems]);

  return (
    <div className={`min-h-screen ${theme.background}`}>
      <div className="max-w-7xl mx-auto px-4 lg:px-0 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            {(groupId || isMyTeams) && (
              <button
                onClick={() => navigate(-1)}
                className={`mr-4 p-2 ${theme.cardHover} rounded-lg transition-colors`}
              >
                <ArrowLeft className={`w-6 h-6 ${theme.textSecondary}`} />
              </button>
            )}
            <div>
              <h1 className={`text-3xl font-bold ${theme.text} mb-2`}>{pageInfo.title}</h1>
              <p className={`${theme.textSecondary}`}>
                {pagination ? `${pagination.totalRecords} teams` : pageInfo.description}
              </p>
            </div>
          </div>

          <button
            onClick={() => navigate(getCreateUrl())}
            className={`${btn.base} ${btn.lg} ${theme.buttonPrimary} flex items-center gap-x-2`}
          >
            <Plus className="w-5 h-5" />
            <span>Create Team</span>
          </button>
        </div>

        {/* Tabs */}
        {showTabs && (
          <div className={`flex border-b ${theme.border} mb-6`}>
            {[
              { key: 'my', label: 'My Teams' },
              { key: 'browse', label: 'Browse Teams' }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-blue-500 text-blue-500'
                    : `border-transparent ${theme.textSecondary} hover:text-blue-400`
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search teams..."
              defaultValue={searchTerm}
              onChange={handleSearchChange}
              className={`w-full pl-10 pr-4 py-3 border ${theme.border} rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.input}`}
            />
          </div>

          {sportTypes.length > 0 && (
            <div className="relative">
              <select
                value={sportTypeId}
                onChange={(e) => setSportTypeId(e.target.value)}
                className={`appearance-none pl-4 pr-10 py-3 border ${theme.border} rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.input} min-w-[160px]`}
              >
                <option value="">All Sports</option>
                {sportTypes.map(s => (
                  <option key={s.id} value={s.id}>{s.sport_name || s.sportName}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          )}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className={`w-8 h-8 border-4 ${theme.link.replace('text-', 'border-')} border-t-transparent rounded-full animate-spin`}></div>
          </div>
        ) : teams.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className={`text-xl font-semibold ${theme.text} mb-2`}>
              {searchTerm || sportTypeId ? 'No teams found' : pageInfo.emptyMessage}
            </h3>
            <p className={`${theme.textSecondary} mb-6`}>
              {searchTerm || sportTypeId ? 'Try adjusting your filters' : pageInfo.emptyDescription}
            </p>
            {!searchTerm && !sportTypeId && (
              <button
                onClick={() => navigate(getCreateUrl())}
                className={`${btn.base} ${btn.lg} ${theme.buttonPrimary} inline-flex items-center gap-x-2`}
              >
                <Plus className="w-5 h-5" />
                <span>Create Team</span>
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {teams.map(team => (
                <TeamCardComponent
                  key={team.id}
                  team={team}
                  baseUrl={baseUrl}
                  onEdit={(t) => navigate(`${baseUrl}/${t.id}/edit`)}
                  onDelete={(id, name) => handleDeleteTeam(id, name)}
                />
              ))}
            </div>

            {pagination?.hasNextPage && (
              <div className="flex justify-center mt-8">
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className={`${btn.base} ${btn.lg} ${theme.buttonSecondary} flex items-center gap-x-2`}
                >
                  {loadingMore ? (
                    <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <ChevronDown className="w-5 h-5" />
                  )}
                  <span>{loadingMore ? 'Loading...' : `Load More (${pagination.totalRecords - teams.length} remaining)`}</span>
                </button>
              </div>
            )}
          </>
        )}

        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={() => setConfirmModal({ isOpen: false, teamId: null, teamName: '' })}
          onConfirm={confirmDeleteTeam}
          title="Delete Team"
          message={`Are you sure you want to delete "${confirmModal.teamName}"? This action cannot be undone.`}
          confirmText="Delete"
          confirmStyle="danger"
          loading={deleteLoading}
        />
      </div>
    </div>
  );
};

export default TeamsListPage;
